// WhatsApp's state, as the app sees it, and the one client for notify-grower.
//
// Jason, 5 Oct 2026: TextMeBot is switched off and outbound WhatsApp is OFF
// until the new (WhatsApp Business) number is live. The switch is ONE row,
// platform_whatsapp_settings, read through whatsapp_status() (mig
// 20261038400000) — the same function the edge sender reads, so a screen and
// the sender cannot disagree. Plan: docs/plans/WHATSAPP-META-CLOUD-API-PLAN-2026-10-05.md §6 Phase 1a.
//
// Both reads FAIL CLOSED: a status that is loading, failed or not the
// function's own shape reads as paused, never as "WhatsApp works".

import { supabase } from '@/lib/supabase'

/** The one sentence. The edge's whatsappOutcome.WHATSAPP_PAUSED_MESSAGE is the same
 *  words (whatsappStatus.test.ts pins the two equal). */
export const WHATSAPP_PAUSED_TEXT = 'WhatsApp is paused until the new number is live'

export type WhatsAppTransport = 'off' | 'textmebot' | 'meta'
export interface WhatsAppStatus { transport: WhatsAppTransport; paused: boolean }

export const WHATSAPP_PAUSED: WhatsAppStatus = Object.freeze({ transport: 'off', paused: true })

const TRANSPORTS: readonly string[] = ['off', 'textmebot', 'meta']

/** whatsapp_status()'s answer, or paused for anything else. PostgREST returns
 *  a one-row function as an object; an array is read too. */
export function parseWhatsAppStatus(data: unknown): WhatsAppStatus {
  const row = (Array.isArray(data) ? data[0] : data) as { transport?: unknown; paused?: unknown } | null | undefined
  const transport = row?.transport
  if (typeof transport !== 'string' || !TRANSPORTS.includes(transport)) return WHATSAPP_PAUSED
  if (typeof row?.paused !== 'boolean') return WHATSAPP_PAUSED
  return { transport: transport as WhatsAppTransport, paused: row.paused || transport === 'off' }
}

// ---------------------------------------------------------------------------
// notify-grower
// ---------------------------------------------------------------------------

const NOTIFY_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-grower`

/**
 * What became of one notify-grower call.
 * - `sent`: WhatsApp took it.
 * - `paused`: nothing was sent — WhatsApp is paused.
 * - `not_sent`: nothing was sent, for the reason in `message`.
 * - `unknown`: the answer did not say (a network error, an old function) — the
 *   screen must not claim it was sent.
 */
export type NotifyResult =
  | { outcome: 'sent' }
  | { outcome: 'paused'; message: string }
  | { outcome: 'not_sent'; message: string }
  | { outcome: 'unknown'; message: string }

/** notify-grower's HTTP answer → a NotifyResult. Only an explicit `sent` on a
 *  2xx is a send; a bare "OK" from the function before Phase 1a is not. */
export function notifyResultFrom(httpOk: boolean, httpStatus: number, body: unknown): NotifyResult {
  const b = (body ?? null) as { status?: unknown; error?: unknown } | null
  const error = typeof b?.error === 'string' && b.error.trim() ? b.error : null
  if (b?.status === 'sent' && httpOk) return { outcome: 'sent' }
  if (b?.status === 'paused') return { outcome: 'paused', message: WHATSAPP_PAUSED_TEXT }
  if (!httpOk) return { outcome: 'not_sent', message: error ?? `WhatsApp message not sent (HTTP ${httpStatus}).` }
  return { outcome: 'unknown', message: "Couldn't confirm whether the WhatsApp message went out." }
}

/** POST to notify-grower as the signed-in user. Never throws. */
export async function notifyGrower(payload: Record<string, unknown>): Promise<NotifyResult> {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch(NOTIFY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token ?? ''}`,
      },
      body: JSON.stringify(payload),
    })
    const body = await res.json().catch(() => null)
    return notifyResultFrom(res.ok, res.status, body)
  } catch {
    // The request may have reached the server and sent before the answer was
    // lost, so this is "don't know", never "not sent".
    return { outcome: 'unknown', message: "Couldn't confirm whether the WhatsApp message went out." }
  }
}
